import { useState } from "react";
import ChurchServiceCard from "../../service/ChurchServiceCard";
import WorshipSongsSection from "../../library/WorshipSongsSection";
import PlaylistsSection from "../../library/PlaylistsSection";
import Markdown from "../../ui/Markdown";

import {
  SAMPLE_SERVICE,
  SAMPLE_SONGS,
  SAMPLE_PLAYLISTS,
} from "../../../sample/launchpad";

// Flattened once here so the specimens can override individual fields to show
// variants, which is the whole point of the gallery.
const SERVICE = {
  title: SAMPLE_SERVICE?.attributes.title ?? "",
  description: SAMPLE_SERVICE?.attributes.description ?? null,
  discussionQuestions: SAMPLE_SERVICE?.attributes.discussionQuestions ?? null,
};

// Component gallery. Each entry renders a web port of an iOS component against
// stubbed data so it can be checked in isolation.
//
// For a whole Launchpad as it will actually appear, see /test.

export default function Library() {
  const [lastAction, setLastAction] = useState<string | null>(null);

  return <main className="min-h-screen bg-gray-100 p-6">
    <div className="mx-auto flex max-w-5xl flex-col gap-10">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Component gallery</h1>
        <p className="text-gray-600">
          Web ports of iOS components, rendered against stubbed data. For a whole
          Launchpad as it will actually appear, see{" "}
          <a href="/test" className="text-accent underline">/test</a>.
        </p>
        <p className="min-h-6 font-mono text-sm text-accent">
          {lastAction ? `action: ${lastAction}` : ""}
        </p>
      </header>

      <Specimen
        name="ChurchServiceCard"
        source="LibraryFeature/View/Component/ChurchServiceCardView.swift"
        note="As used by FeaturedServiceView on the home screen. The card caps at 500px and centres beyond that; below it the wrapper padding holds it off the edge while the card shrinks."
      >
        <div className="flex flex-col gap-8">
          <Group title="Widths">
            <Frame label="900px container — capped at 500px" width={900}>
              <Sample onAction={setLastAction} />
            </Frame>

            <Frame label="393px (iPhone 17 Pro)" width={393}>
              <Sample onAction={setLastAction} />
            </Frame>

            <Frame label="320px (small phone) — actions stack" width={320}>
              <Sample onAction={setLastAction} />
            </Frame>
          </Group>

          <Group title="Variants">
            <Frame label="No discussion questions" width={393}>
              <Sample onAction={setLastAction} discussionQuestions={null} />
            </Frame>

            <Frame label="No thumbnail, no description" width={393}>
              <Sample onAction={setLastAction} thumbnailUrl={null} description={null} />
            </Frame>
          </Group>
        </div>
      </Specimen>

      <Specimen
        name="TileSection — WorshipSongsSection / PlaylistsSection"
        source="LibraryFeature/View/Component/TileSectionView.swift"
        note="Both sections are thin wrappers over one TileSection, exactly as WorshipSongsListView and PlaylistListView are in the app. Scrollers bleed to the container edges and fade against the page background; the fade only appears when there is more to scroll to."
      >
        <div className="flex flex-col gap-8">
          <Group title="Scroller (compact)">
            <Frame label="393px (iPhone 17 Pro)" width={393}>
              <div className="flex flex-col gap-5">
                <WorshipSongsSection
                  songs={SAMPLE_SONGS}
                  onSongClick={(s) => setLastAction(`song: ${s.attributes.title}`)}
                />
                <PlaylistsSection
                  playlists={SAMPLE_PLAYLISTS}
                  onPlaylistClick={(p) => setLastAction(`playlist: ${p.attributes.name}`)}
                />
              </div>
            </Frame>
          </Group>

          <Group title="Grid (usesGrid, for wide layouts)">
            <Frame label="560px" width={560}>
              <div className="flex flex-col gap-5">
                <WorshipSongsSection
                  songs={SAMPLE_SONGS}
                  onSongClick={(s) => setLastAction(`song: ${s.attributes.title}`)}
                  usesGrid
                />
                <PlaylistsSection
                  playlists={SAMPLE_PLAYLISTS}
                  onPlaylistClick={(p) => setLastAction(`playlist: ${p.attributes.name}`)}
                  usesGrid
                />
              </div>
            </Frame>
          </Group>
        </div>
      </Specimen>

      <Specimen
        name="Markdown"
        source="LibraryFeature/.../Screen/DiscussionQuestionsView.swift (MarkdownView)"
        note="Discussion questions are authored as markdown. Sample content currently has none, so this exercises the elements that can appear."
      >
        <Frame label="393px" width={393}>
          <div className="rounded-lg bg-surface p-4">
            <Markdown>{MARKDOWN_SAMPLE}</Markdown>
          </div>
        </Frame>
      </Specimen>
    </div>
  </main>
}

const MARKDOWN_SAMPLE = `## Getting started

Read **1 Peter 4:7-11** together, then work through these.

1. Which gift comes most naturally to you?
2. Which feels hardest to offer?

- Minds
- Hearts
- *Words* and actions

> Each of you should use whatever gift you have received to serve others.

Full passage on [Bible Gateway](https://www.biblegateway.com).`;

// Renders the sample service, with any field overridable to show a variant.
function Sample(props: {
  onAction: (action: string) => void,
  thumbnailUrl?: string | null,
  description?: string | null,
  discussionQuestions?: string | null
}) {
  return <ChurchServiceCard
    thumbnailUrl={props.thumbnailUrl === undefined ? null : props.thumbnailUrl}
    title={SERVICE.title}
    description={props.description === undefined ? SERVICE.description : props.description}
    discussionQuestions={
      props.discussionQuestions === undefined
        ? SERVICE.discussionQuestions
        : props.discussionQuestions
    }
    onDiscussionQuestionsClick={() => props.onAction("discussionQuestions")}
    onDetailsClick={() => props.onAction("details")}
  />
}

function Group(props: { title: string, children: React.ReactNode }) {
  return <div className="flex flex-col gap-3">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
      {props.title}
    </h3>
    <div className="flex flex-wrap items-start gap-8">{props.children}</div>
  </div>
}

function Specimen(props: {
  name: string,
  source: string,
  note?: string,
  children: React.ReactNode
}) {
  return <section className="flex flex-col gap-4">
    <div className="flex flex-col gap-1 border-b border-separator pb-2">
      <h2 className="font-mono text-lg font-semibold">{props.name}</h2>
      <p className="font-mono text-xs text-gray-500">{props.source}</p>
      {props.note && <p className="text-sm text-gray-600">{props.note}</p>}
    </div>
    {props.children}
  </section>
}

function Frame(props: { label: string, width: number, children: React.ReactNode }) {
  return <div className="flex flex-col gap-2">
    <span className="font-mono text-xs text-gray-500">{props.label}</span>
    <div style={{ width: props.width }}>{props.children}</div>
  </div>
}
